'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/lib/auth/context';
import StartFreeButton from '@/components/home/StartFreeButton';

// Shared marketing nav — same on every logged-out page except /courts, which
// swaps Guides/FAQ for a static "All Courts" current-page indicator.
const HOME_LINKS = [
  { href: '/#how', label: 'How it works' },
  { href: '/#features', label: 'Features' },
  { href: '/#coverage', label: 'Coverage' },
  { href: '/#pricing', label: 'Pricing' },
  { href: '/#guides', label: 'Guides' },
  { href: '/#faq', label: 'FAQ' },
];

const COURTS_LINKS = [
  { href: '/#how', label: 'How it works' },
  { href: '/#features', label: 'Features' },
  { href: '/#coverage', label: 'Coverage' },
  { href: '/#pricing', label: 'Pricing' },
];

export default function Navbar() {
  const pathname = usePathname();
  const isCourtsPage = pathname === '/courts';
  const navLinks = isCourtsPage ? COURTS_LINKS : HOME_LINKS;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLawyer, setIsLawyer] = useState(false);
  const { isAuthenticated, isLoading, signOut } = useAuth();

  // Fetch subscription tier to determine if calendar should be shown
  useEffect(() => {
    let cancelled = false;

    async function loadCalendarAccess() {
      await Promise.resolve();
      if (cancelled) return;

      if (!isAuthenticated) {
        setIsLawyer(false);
        return;
      }

      try {
        const response = await fetch('/api/subscription');
        const data = response.ok ? await response.json() : null;
        if (!cancelled) {
          setIsLawyer(
            !!data?.isTrial || ['lawyer_50', 'lawyer_pro', 'firm', 'corporate'].includes(data?.subscription?.tier),
          );
        }
      } catch {
        if (!cancelled) setIsLawyer(false);
      }
    }

    loadCalendarAccess();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const handleLogout = async () => {
    await signOut();
    window.location.href = '/';
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/[0.97] border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-[76px]">
          <div className="flex items-center">
            <Link href={isAuthenticated ? '/dashboard' : '/'} className="flex items-center gap-3" aria-label="India Case Status home">
              <Image
                src="/logo.png"
                alt=""
                width={42}
                height={42}
                className="rounded-full"
                priority
              />
              <div className="hidden sm:flex flex-col">
                <span className="font-bold text-base text-primary leading-[1.15]">
                  India Case Status
                </span>
                <span className="text-[11px] text-gray-500 hidden sm:block">
                  Never miss a hearing.
                </span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-[34px]">
            {!isAuthenticated && navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-[13px] font-medium tracking-[0.01em] text-gray-600 hover:text-primary transition-colors"
              >
                {link.label}
              </Link>
            ))}
            {!isAuthenticated && isCourtsPage && (
              <span className="text-[13px] font-semibold tracking-[0.01em] text-primary border-b-2 border-accent-500 pb-0.5">
                All Courts
              </span>
            )}
            {!isLoading && (
              isAuthenticated ? (
                <div className="flex items-center space-x-3">
                  <Link
                    href="/dashboard"
                    className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Dashboard
                  </Link>
                  {isLawyer && (
                    <Link
                      href="/calendar"
                      className="text-gray-600 hover:text-primary p-2 transition-colors"
                      title="Calendar"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </Link>
                  )}
                  <Link
                    href="/settings"
                    className="text-gray-600 hover:text-primary p-2 transition-colors"
                    title="Settings"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="text-gray-600 hover:text-red-600 px-3 py-2 transition-colors"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Link
                    href="/login"
                    className="text-sm font-medium text-gray-600 hover:text-primary transition-colors px-3 py-2"
                  >
                    Sign in
                  </Link>
                  <StartFreeButton className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent text-white px-5 py-2.5 rounded-[10px] text-sm font-semibold shadow-[0_10px_15px_-3px_rgba(184,134,11,0.2),0_4px_6px_-4px_rgba(184,134,11,0.2)] transition-colors" />
                </div>
              )
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-1">
            {!isLoading && !isAuthenticated && pathname !== '/add-case' && (
              <Link
                href="/add-case"
                className="inline-flex min-h-11 items-center justify-center rounded-lg bg-primary px-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
                onClick={() => setIsMenuOpen(false)}
              >
                Search case
              </Link>
            )}
            {!isLoading && isAuthenticated && (
              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-600 hover:bg-red-50 hover:text-red-600"
                aria-label="Logout"
                title="Logout"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
              aria-label="Toggle menu"
              aria-controls="public-mobile-navigation"
              aria-expanded={isMenuOpen}
            >
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div id="public-mobile-navigation" className="md:hidden border-t border-gray-100 bg-white">
          <div className="px-4 py-4 space-y-3">
            {!isAuthenticated && (
              <Link
                href="/add-case"
                className="block min-h-12 rounded-xl bg-primary px-4 py-3 text-center text-white shadow-sm transition-colors hover:bg-primary-700"
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="block font-semibold">Search my case</span>
                <span className="mt-0.5 block text-xs text-white/80">Use CNR, case number, or party name</span>
              </Link>
            )}
            {!isAuthenticated && navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="block text-gray-600 hover:text-primary transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            {!isAuthenticated && isCourtsPage && (
              <span className="block text-primary font-semibold">All Courts</span>
            )}
            {!isLoading && (
              isAuthenticated ? (
                <div className="space-y-2">
                  <Link
                    href="/dashboard"
                    className="block bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors text-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  {isLawyer && (
                    <Link
                      href="/calendar"
                      className="block text-gray-600 hover:text-primary px-4 py-2 transition-colors text-center border border-gray-200 rounded-lg"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Calendar
                    </Link>
                  )}
                  <Link
                    href="/settings"
                    className="block text-gray-600 hover:text-primary px-4 py-2 transition-colors text-center border border-gray-200 rounded-lg"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Settings
                  </Link>
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleLogout();
                    }}
                    className="block w-full text-red-600 hover:text-red-700 px-4 py-2 transition-colors text-center border border-red-200 rounded-lg"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Link
                    href="/login"
                    className="block text-gray-600 hover:text-primary px-4 py-2 transition-colors text-center border border-gray-200 rounded-lg"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Sign in
                  </Link>
                  <StartFreeButton
                    className="block bg-accent-500 hover:bg-accent text-white px-4 py-2 rounded-lg transition-colors text-center font-semibold"
                    onClick={() => setIsMenuOpen(false)}
                    unauthenticatedLabel="Create account to track"
                  />
                </div>
              )
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
